import { Component, type ErrorInfo, type ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { ErrorState } from "../ErrorState/ErrorState";
import { Illustration } from "../../illustrations/Illustration";
import type { IllustrationName } from "../../illustrations/registry";

export type SkeletonVariant = "list" | "cards" | "table" | "detail";

export interface SkeletonProps {
  readonly variant?: SkeletonVariant;
  /** Number of placeholder rows/cards (default 5 for lists, 3 for cards). */
  readonly rows?: number;
  readonly className?: string;
}

const bar = "rounded-md bg-surface-sunken";

/**
 * Grey placeholder shaped like the content that is loading. `aria-busy` + a visually hidden
 * "Loading…" so screen readers hear one announcement instead of a stream of empty boxes.
 */
export function Skeleton({ variant = "list", rows, className }: SkeletonProps) {
  const { t } = useI18n();
  const count = rows ?? (variant === "cards" ? 3 : 5);
  const items = Array.from({ length: count }, (_, i) => i);
  return (
    <div
      role="status"
      aria-busy="true"
      data-testid="async-skeleton"
      data-variant={variant}
      // UXR-33 contract: list pages expose `[data-list-skeleton]` while their rows load.
      {...(variant === "list" || variant === "table" ? { "data-list-skeleton": "" } : {})}
      className={cx("w-full motion-safe:animate-pulse", className)}
    >
      <span className="sr-only">{t("ui.async.loading")}</span>
      {variant === "cards" ? (
        <div aria-hidden className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((i) => (
            <div key={i} className="flex flex-col gap-2 rounded-lg border border-line p-4">
              <div className={cx(bar, "h-4 w-2/3")} />
              <div className={cx(bar, "h-3 w-full")} />
              <div className={cx(bar, "h-3 w-5/6")} />
            </div>
          ))}
        </div>
      ) : variant === "detail" ? (
        <div aria-hidden className="flex flex-col gap-3">
          <div className={cx(bar, "h-6 w-1/3")} />
          <div className={cx(bar, "h-4 w-full")} />
          <div className={cx(bar, "h-4 w-11/12")} />
          <div className={cx(bar, "h-32 w-full")} />
        </div>
      ) : (
        <ul aria-hidden className="flex flex-col gap-2">
          {items.map((i) => (
            <li
              key={i}
              className={cx(
                "flex items-center gap-3",
                variant === "table" ? "border-b border-line py-2" : "py-1",
              )}
            >
              {variant === "list" ? (
                <div className="size-9 shrink-0 rounded-full bg-surface-sunken" />
              ) : null}
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className={cx(bar, "h-3.5", i % 2 === 0 ? "w-1/2" : "w-2/5")} />
                <div className={cx(bar, "h-3", i % 2 === 0 ? "w-4/5" : "w-3/4")} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface AsyncEmptyProps {
  readonly title: string;
  readonly body?: string;
  /** Next step (a button or link) so an empty page is never a dead end. */
  readonly action?: ReactNode;
  readonly illustration?: IllustrationName;
  readonly className?: string;
}

/** Plain empty state: what this page is for and the one next step. */
export function AsyncEmpty({ title, body, action, illustration, className }: AsyncEmptyProps) {
  return (
    <div
      data-testid="async-empty"
      className={cx(
        "flex flex-col items-center gap-2 rounded-lg border border-dashed border-line p-6 text-center",
        className,
      )}
    >
      {illustration ? <Illustration name={illustration} size={96} /> : null}
      <p className="text-base font-semibold text-fg">{title}</p>
      {body ? <p className="max-w-prose text-sm text-fg-muted">{body}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/** Support reference of a failed request: `error.requestId` when the API client set one. */
export function requestIdOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const id = (error as { requestId?: unknown }).requestId;
  return typeof id === "string" && id !== "" ? id : null;
}

export interface AsyncBoundaryProps<T> {
  /** True while the first load runs (a refetch with data keeps showing the data). */
  readonly loading: boolean;
  /** Any truthy value = the load failed. */
  readonly error?: unknown;
  readonly data?: T | null | undefined;
  /** When it returns true the `empty` slot renders instead of `children`. */
  readonly isEmpty?: (data: T) => boolean;
  readonly empty?: ReactNode;
  readonly skeleton?: SkeletonVariant | ReactNode;
  readonly onRetry?: () => void;
  /** Replaces the default error card (the dashboard plugs its `ApiErrorState`). */
  readonly renderError?: (error: unknown, retry: (() => void) | undefined) => ReactNode;
  readonly children: ReactNode | ((data: T) => ReactNode);
}

function DefaultError({ error, onRetry }: { error: unknown; onRetry?: (() => void) | undefined }) {
  const { t } = useI18n();
  const id = requestIdOf(error);
  return (
    <ErrorState
      title={t("ui.async.errorTitle")}
      message={t("ui.async.errorBody")}
      {...(id ? { reference: t("ui.async.reference", { id }) } : {})}
      {...(onRetry ? { onRetry, retryLabel: t("ui.async.retry") } : {})}
    />
  );
}

interface CatchProps {
  readonly fallback: (error: unknown, reset: () => void) => ReactNode;
  readonly children: ReactNode;
}

/** Render-time crash guard: one broken widget shows an error card, not a blank page. */
class Catch extends Component<CatchProps, { error: unknown; failed: boolean }> {
  override state = { error: null as unknown, failed: false };
  static getDerivedStateFromError(error: unknown) {
    return { error, failed: true };
  }
  override componentDidCatch(error: unknown, info: ErrorInfo) {
    // Surfaced to the console for the route sweep (IDEA-12); no secrets are in render errors.
    console.error("AsyncBoundary caught a render error", error, info.componentStack);
  }
  reset = () => this.setState({ error: null, failed: false });
  override render() {
    return this.state.failed
      ? this.props.fallback(this.state.error, this.reset)
      : this.props.children;
  }
}

/**
 * IDEA-09: one pattern for every data page — skeleton while loading, error card with the
 * support reference (request id) and Retry, a real empty state, then the content. Render
 * crashes inside the content are caught too, so a page never goes blank.
 */
export function AsyncBoundary<T>({
  loading,
  error,
  data,
  isEmpty,
  empty,
  skeleton = "list",
  onRetry,
  renderError,
  children,
}: AsyncBoundaryProps<T>) {
  const showError = (e: unknown, retry: (() => void) | undefined) =>
    renderError ? renderError(e, retry) : <DefaultError error={e} onRetry={retry} />;
  const hasData = data !== undefined && data !== null;
  if (error && !hasData) return <>{showError(error, onRetry)}</>;
  if (loading && !hasData) {
    return typeof skeleton === "string" ? (
      <Skeleton variant={skeleton as SkeletonVariant} />
    ) : (
      <>{skeleton}</>
    );
  }
  if (hasData && isEmpty?.(data) && empty !== undefined) return <>{empty}</>;
  return (
    <Catch
      fallback={(e, reset) =>
        showError(e, () => {
          reset();
          onRetry?.();
        })
      }
    >
      {typeof children === "function"
        ? hasData
          ? (children as (d: T) => ReactNode)(data)
          : null
        : children}
    </Catch>
  );
}
